/**
 * OnboardingSteps — the original step registry.
 *
 * CORE-BL: the MANAGER and USER libraries below are the pre-existing,
 * populated content and are left as they are. The role-specific tracks
 * (technician, front of house, parts, read-only, organisation overview) live
 * in `src/lib/onboarding/roleExperienceSteps.ts` alongside their audience
 * metadata, and `resolveExperienceSteps` is the single resolver that decides
 * what a given person actually sees.
 *
 * `TECHNICIAN_STEPS` is retained as an empty export ONLY so that anything
 * still importing it keeps compiling. It is no longer what a technician is
 * shown, and nothing routes to it.
 */
import type { OnboardingExperience } from '@/lib/onboarding/onboardingExperience';
// Runtime import is safe in this direction: roleExperienceSteps imports
// OnboardingStep as a TYPE only, which is erased, so no cycle exists at
// runtime.
import {
  DETAIL_EXPERIENCE_STEPS,
  MANAGER_OPS_EXPERIENCE_STEPS,
  FOH_EXPERIENCE_STEPS,
  ORG_OVERVIEW_EXPERIENCE_STEPS,
  PARTS_EXPERIENCE_STEPS,
  READONLY_EXPERIENCE_STEPS,
  TECHNICIAN_EXPERIENCE_STEPS,
} from '@/lib/onboarding/roleExperienceSteps';

/** The role-specific tracks, unfiltered. */
const getRoleExperienceSteps = (flow: OnboardingExperience): OnboardingStep[] => {
  switch (flow) {
    case 'technician_core_v1':
      return TECHNICIAN_EXPERIENCE_STEPS;
    case 'foh_service_writer':
    case 'foh_dispatcher':
      // One library, two durable identities: applicability gives the two
      // roles different sequences, so their progress must not be shared.
      return FOH_EXPERIENCE_STEPS;
    case 'parts_core_v1':
      return PARTS_EXPERIENCE_STEPS;
    case 'readonly_core_v1':
      return READONLY_EXPERIENCE_STEPS;
    case 'org_overview_v1':
      return ORG_OVERVIEW_EXPERIENCE_STEPS;
    case 'detail_core_v1':
      return DETAIL_EXPERIENCE_STEPS;
    case 'manager_ops_v1':
      return MANAGER_OPS_EXPERIENCE_STEPS;
    default:
      // setup_wizard belongs to CORE-W, and the LEGACY 'technician' key
      // genuinely had no content — that is what its historical rows record.
      return [];
  }
};

export interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  page: string; // Route path
  gateType: 'hard' | 'soft'; // Hard gates block progress, soft gates are informational
  coachmarks: string[]; // data-testid selectors for UI anchors
  icon?: string; // Optional Lucide icon name for visual clarity
}

// Manager Flow - Expanded with granular shop profile steps
export const MANAGER_STEPS: OnboardingStep[] = [
  {
    id: 'click-profile',
    title: 'Open Shop Profile',
    description: 'Click the Profile button to configure your shop',
    page: '/',
    gateType: 'soft',
    coachmarks: ['nav-profile-button'],
    icon: 'User',
  },
  {
    id: 'general-info',
    title: 'Fill General Information',
    description: 'Enter your shop name, time zone, and complete address. The time zone is what every scheduled time in the shop is expressed in, and the address is how weather is located for your bays',
    page: '/shop-profile',
    // HARD GATE — harm justified. Without a time zone every scheduled time
    // the shop records is expressed in the wrong one, and that error is
    // silent and retroactive: it corrupts the operational truth rather than
    // producing a visible failure. This is the class the constitution has in
    // mind — "a shop without configured bays cannot schedule sensibly".
    gateType: 'hard',
    coachmarks: ['general-info-card'],
    icon: 'Building',
  },
  {
    id: 'vehicle-packages',
    title: 'Select Vehicle Packages',
    description: 'Choose the types of vehicles your shop services',
    page: '/shop-profile',
    // DOWNGRADED to soft. This shapes how much of the job catalogue is
    // offered; it does not decide whether anything the shop records is
    // CORRECT. A shop can take in a vehicle, work it and complete it without
    // having chosen packages, so blocking exploration here enforced a
    // preference rather than preventing harm.
    gateType: 'soft',
    coachmarks: ['vehicle-packages-card'],
    icon: 'Car',
  },
  {
    id: 'outdoor-weather-settings',
    title: 'Configure Outdoor Weather Rules',
    description: 'Set the temperature and conditions (rain, snow, storms) your shop treats as incompatible with outdoor work. OptiBay raises an advisory when the reported or forecast weather crosses your thresholds, and shows you the numbers behind it — outdoor bays stay available and the decision stays yours',
    page: '/shop-profile',
    gateType: 'soft',
    coachmarks: ['outdoor-weather-card'],
    icon: 'CloudRain',
  },
  {
    id: 'click-hours-tab',
    title: 'Navigate to Hours & Holidays',
    description: 'Click the Hours & Holidays tab to configure your schedule',
    page: '/shop-profile',
    gateType: 'soft',
    coachmarks: ['shopprofile-tab-hours'],
    icon: 'Clock',
  },
  {
    id: 'shop-hours',
    title: 'Set Business Hours',
    description: 'Configure your shop operating hours',
    page: '/shop-profile?tab=hours',
    // HARD GATE — harm justified. Scheduling places work inside the shop's
    // operating day. With no day defined, placement has nothing to be
    // correct against and work lands at times the shop is not open.
    gateType: 'hard',
    coachmarks: ['business-hours-card'],
    icon: 'Calendar',
  },
  {
    id: 'holidays',
    title: 'Mark Holidays',
    description: 'Select days when your shop is closed',
    page: '/shop-profile?tab=hours',
    gateType: 'soft',
    coachmarks: ['holidays-card'],
    icon: 'CalendarOff',
  },
  {
    id: 'click-culture-tab',
    title: 'Navigate to Shop Culture',
    description: 'Click the Shop Culture tab to configure preferences',
    page: '/shop-profile?tab=hours',
    gateType: 'soft',
    coachmarks: ['shopprofile-tab-culture'],
    icon: 'Settings',
  },
  {
    id: 'shop-culture',
    title: 'Configure Shop Culture',
    description: 'Fine-tune scheduling behavior: willingness to reassign tickets, workload balance preferences, comeback nudge sensitivity, and ticket splitting rules',
    page: '/shop-profile?tab=culture',
    gateType: 'soft',
    coachmarks: ['culture-settings-card'],
    icon: 'Heart',
  },
  {
    id: 'save-profile',
    title: 'Save Your Profile',
    description: 'Click Save Profile to finalize your shop configuration',
    page: '/shop-profile?tab=culture',
    gateType: 'soft',
    coachmarks: ['business-hours-save'],
    icon: 'Save',
  },
  {
    id: 'staff-schedules-tab',
    title: 'Staff Schedules (Optional)',
    description: 'The Staff Schedules tab lets you set working hours for managers and service writers. These are used for time clock reporting and payroll — they do not affect ticket scheduling.',
    page: '/shop-profile?tab=staffschedules',
    gateType: 'soft',
    coachmarks: ['shopprofile-tab-staffschedules', 'staff-schedule-manager'],
    icon: 'Calendar',
  },
  {
    id: 'admin-settings-tab',
    title: 'Explore Admin Settings',
    description: 'The Admin Settings tab is your hub for enabling add-ons (OptiAssets, Connect, Multi-Team), accessing Payroll and Time Clock reports, configuring customer reminders, and managing tools like Inspection Templates.',
    page: '/shop-profile?tab=admin',
    gateType: 'soft',
    coachmarks: ['shopprofile-tab-admin'],
    icon: 'Settings',
  },
  {
    id: 'click-birdseye',
    title: 'Open Birdseye View',
    description: 'Click the Birdseye button to access the board',
    page: '/',
    gateType: 'soft',
    coachmarks: ['birdseye-link'],
    icon: 'Eye',
  },
  {
    id: 'click-configure-layout',
    title: 'Click Configure Layout',
    description: 'Click the Configure Layout button to set up your bays',
    page: '/birdseye',
    gateType: 'soft',
    coachmarks: ['configure-layout-btn'],
    icon: 'Layout',
  },
  {
    id: 'create-bay',
    title: 'Add Your First Bay',
    description: 'Choose a bay type from the Tools panel to add your first bay to the layout.',
    page: '/shop-floor/editor',
    gateType: 'soft',
    coachmarks: [
      'facility-tool-palette',
    ],
    icon: 'Plus',
  },
  {
    id: 'edit-bay',
    title: 'Edit Your Bay',
    description: 'Click a bay from the list to select it, then configure its name, type, and assigned technician in the editor below.',
    page: '/shop-floor/editor',
    gateType: 'soft',
    coachmarks: ['facility-inspector-panel'],
    icon: 'Settings',
  },
  {
    id: 'configure-shop-layout',
    title: 'Configure Shop Layout',
    description: 'Build the birdseye view of your shop by setting up your bays and layout',
    page: '/shop-floor/editor',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'save-layout',
    title: 'Save Your Layout',
    description: 'Click Save Layout to save your shop floor configuration',
    page: '/shop-floor/editor',
    gateType: 'soft',
    coachmarks: ['save-layout-button'],
  },
  {
    id: 'click-team',
    title: 'Open Team Management',
    description: 'Open Shop Profile and switch to the Teams tab to invite and manage team members.',
    page: '/',
    gateType: 'soft',
    // Top-bar `nav-team-button` was removed; the Team flow now lives
    // inside Shop Profile, so the coachmark points at the Profile entry.
    coachmarks: ['nav-profile-button'],
    icon: 'Users',
  },
  {
    id: 'invite-member',
    title: 'Invite a Team Member',
    description: 'Send your first team invitation',
    page: '/team-management',
    // DOWNGRADED to soft. Nothing about a shop's operational record is
    // misrepresented because a second person has not been invited yet — a
    // one-person shop is a real shop. Blocking setup until somebody else is
    // added made an invitation a precondition for using the product, which
    // is guidance wearing a gate's clothes.
    gateType: 'soft',
    coachmarks: [
      'invite-member-button',
      'invite-role-select',
      'send-invite-button',
    ],
    icon: 'UserPlus',
  },
  {
    id: 'check-email',
    title: 'Check Email for Invitation',
    description: 'Ask your teammate to check their email and accept the invitation',
    page: '/team-management',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'view-accepted-tech',
    title: 'View & Edit Accepted Technician',
    description: 'Once your teammate accepts, they will appear in the team list',
    page: '/team-management',
    gateType: 'soft',
    coachmarks: ['team-members-table'],
  },
  {
    id: 'navigate-to-profile',
    title: 'Open Shop Profile',
    description: 'Click the Profile button in the navigation to continue',
    page: '/team-management',
    gateType: 'soft',
    coachmarks: ['nav-profile-button'],
  },
  {
    id: 'click-technicians-tab',
    title: 'Navigate to Technicians',
    description: 'Click the Technicians tab in the shop profile',
    page: '/shop-profile',
    gateType: 'soft',
    coachmarks: ['shopprofile-tab-technicians'],
  },
  {
    id: 'click-edit-tech',
    title: 'Edit Technician Profile',
    description: 'Click the edit button on your technician to configure their profile',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['edit-tech-button'],
  },
  {
    id: 'tech-general-info',
    title: 'Edit Technician Information',
    description: 'Review and edit the technician\'s basic information',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-basic-info-section'],
  },
  {
    id: 'click-skills-tab',
    title: 'Navigate to Skills & Proficiencies',
    description: 'Click the Skills & Proficiencies tab',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-skills-tab'],
  },
  {
    id: 'tech-proficiencies',
    title: 'Set Skill Proficiencies',
    description: 'Configure the technician\'s skill levels for different job types',
    page: '/shop-profile?tab=technicians',
    // DOWNGRADED to soft. Proficiency improves assignment SUGGESTIONS; it is
    // not a precondition for representing work correctly, and an unset
    // proficiency is already treated as unknown rather than as zero. Holding
    // a manager at this step taught that the product will not proceed
    // without an opinion it can perfectly well operate without.
    gateType: 'soft',
    coachmarks: ['tech-proficiencies-section'],
  },
  {
    id: 'click-schedule-tab',
    title: 'Navigate to Bays',
    description: 'Click the Bays tab',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-schedule-tab'],
  },
  {
    id: 'tech-schedule',
    title: 'Configure Bays',
    description: 'Set up the technician\'s bay assignments',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-bay-assignments-section'],
  },
  {
    id: 'click-hours-timeoff-tab',
    title: 'Navigate to Hours & Time Off',
    description: 'Click the Hours & Time Off tab to configure the technician\'s schedule',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-hours-tab'],
  },
  {
    id: 'tech-working-hours',
    title: 'Set Working Days & Hours',
    description: 'Configure which days the technician works and their daily hours',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-hours-timeoff-section'],
  },
  {
    id: 'tech-days-unavailable',
    title: 'Mark Days Unavailable',
    description: 'Set time off and unavailable dates for this technician',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-days-unavailable-section'],
  },
  {
    id: 'tech-recurring-breaks',
    title: 'Configure Recurring Breaks',
    description: 'Add daily breaks that occur every working day (e.g., lunch)',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-recurring-breaks-section'],
  },
  {
    id: 'tech-non-recurring-breaks',
    title: 'Add Non-Recurring Breaks',
    description: 'Set one-time breaks for appointments or special events',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['tech-non-recurring-breaks-section'],
  },
  {
    id: 'save-tech-profile',
    title: 'Save Technician Profile',
    description: 'Click Save to finalize the technician setup',
    page: '/shop-profile?tab=technicians',
    gateType: 'soft',
    coachmarks: ['save-tech-button'],
  },
  {
    id: 'analytics-intro-manager',
    title: 'Monitor Shop Performance',
    description: 'Now that your shop is set up, use the Analytics Dashboard to track technician performance, advisor activity, and shop health metrics.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['nav-analytics-button'],
    icon: 'BarChart3',
  },
  {
    id: 'analytics-overview-manager',
    title: 'Analytics Dashboard Overview',
    description: 'Explore the Analytics tabs: Technicians (performance metrics), Advisors (ticket tracking), Jobs (category analysis), Performance (job/vehicle/year breakdowns), Pause Analysis (pause time tracking), Trends (historical charts), Shop Health (manager dashboard), and Alerts (notifications).',
    page: '/analytics',
    gateType: 'soft',
    coachmarks: [],
    icon: 'Activity',
  },
  {
    id: 'user-guide-intro',
    title: 'Access the User Guide',
    description: 'Click the account menu button in the top right corner to find the User Guide.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-menu-button'],
    icon: 'BookOpen',
  },
  {
    id: 'user-guide-select',
    title: 'Open the User Guide',
    description: 'Click "User Guide" to access detailed documentation for every feature.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['user-guide-link'],
    icon: 'BookOpen',
  },
  {
    id: 'user-guide-review',
    title: 'Review the User Guide',
    description: 'Take a moment to explore the User Guide. It covers every feature in detail and is always available from the account menu whenever you need help.',
    page: '/user-guide',
    gateType: 'soft',
    coachmarks: [],
    icon: 'BookOpen',
  },
  {
    id: 'setup-complete',
    title: 'Setup Complete!',
    description: 'Your shop is configured and ready to go! You can always revisit these settings from the Profile button. Check the User Guide for detailed help on any feature. The User Tutorial will walk you and your team through day-to-day operations.',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
    icon: 'CheckCircle',
  },
];

// Technician Flow - No tutorials for technicians
/**
 * @deprecated CORE-BL — a technician is taught from
 * `TECHNICIAN_EXPERIENCE_STEPS`. This empty array is what six of ten roles
 * were routed into; it is kept only for import compatibility and is never
 * selected.
 */
export const TECHNICIAN_STEPS: OnboardingStep[] = [];

// User Flow - 19 Steps for day-to-day operations
export const USER_STEPS: OnboardingStep[] = [
  {
    id: 'welcome-dayboard',
    title: 'Welcome to Your Daily Board',
    description: 'This is your command center for managing daily schedules and work orders',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'date-navigation',
    title: 'Navigate Between Days',
    description: 'Use the date picker to view different days\' schedules',
    page: '/',
    gateType: 'soft',
    coachmarks: ['inline-calendar'],
  },
  {
    id: 'overview-tab',
    title: 'Overview Tab',
    description: 'Your shop\'s daily snapshot: active tickets, pending work, completed count, and the OBLI load indicator showing how stressed or smooth your day is',
    page: '/',
    gateType: 'soft',
    coachmarks: ['overview-tab'],
  },
  {
    id: 'shop-overview-stats',
    title: 'Shop Overview Dashboard',
    description: 'At-a-glance stats, technician workloads, and the OBLI load indicator to quickly assess your shop\'s daily status',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'create-ticket-button',
    title: 'Create New Tickets',
    description: 'Add new work orders to the schedule',
    page: '/',
    gateType: 'soft',
    coachmarks: ['create-ticket-btn'],
    icon: 'Ticket',
  },
  {
    id: 'import-csv-button',
    title: 'Import CSV',
    description: 'Bulk import multiple tickets at once from a CSV file for faster data entry',
    page: '/create-ticket',
    gateType: 'soft',
    coachmarks: ['import-csv-button'],
  },
  {
    id: 'upload-ro',
    title: 'Upload Repair Order',
    description: 'Scan or upload a repair order document to auto-fill vehicle and job details using AI',
    page: '/create-ticket',
    gateType: 'soft',
    coachmarks: ['ro-upload-section'],
  },
  {
    id: 'create-ticket-page',
    title: 'Ticket Creation Form',
    description: 'We have prefilled the basic vehicle and customer information for this tutorial. Review the Vehicle Information and Customer & Ticket Details sections to see how ticket data is organized.',
    page: '/create-ticket',
    gateType: 'soft',
    coachmarks: ['ticket-form-sections'],
  },
  {
    id: 'select-job',
    title: 'Add Jobs to Ticket',
    description: 'We have auto-selected a sample job with custom hours and notes. Review the job details, then click "Add Job" to add it to the ticket.',
    page: '/create-ticket',
    gateType: 'soft',
    coachmarks: ['jobs-selection-section', 'add-job-button'],
  },
  {
    id: 'review-current-jobs',
    title: 'Review Current Jobs',
    description: 'This section shows all jobs added to the ticket. You can see the total time estimate and make any final adjustments. When ready, click "Create Ticket" to submit.',
    page: '/create-ticket',
    gateType: 'soft',
    coachmarks: ['current-jobs-section'],
  },
  {
    id: 'optiplace-modal',
    title: 'OptiAssign - OptiPlace',
    description: 'Choose where to place this ticket. Use the date selector in the top right to schedule for a different day. Check "Vehicle Present" if the vehicle is already at the shop. Press "Create Ticket with Assignment" to continue.',
    page: '/create-ticket',
    gateType: 'soft',
    coachmarks: ['optiplace-date-selector', 'vehicle-present-checkbox', 'optiplace-options'],
  },
  {
    id: 'return-to-board',
    title: 'Back to the Board',
    description: 'Return to the dashboard to see your work orders',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'technician-columns',
    title: 'Technician Schedule Columns',
    description: 'Each technician card shows: Name & Role, Presence status (Present/Absent/Off), Daily schedule times, Lunch/break periods, Efficiency & Buffer percentages, Bay assignments (e.g., 2-Post Lift), and real-time Workload with active ticket count',
    page: '/',
    gateType: 'soft',
    coachmarks: ['technician-column-1'],
  },
  {
    id: 'ticket-command-center',
    title: 'Open Ticket Command Center',
    description: 'Click the ticket number (e.g. #TKT-0001) on any ticket card to open its Command Center — a detailed view with everything you need to manage that ticket.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-number'],
    icon: 'Hash',
  },
  {
    id: 'command-center-details',
    title: 'Command Center — Details Tab',
    description: 'The Details tab shows customer & vehicle info, timing (start time, due out, Tech OptiTime, elapsed time, OptiFinish), technician assignment, bay info, and all jobs on the ticket. Use the Edit buttons to update customer, vehicle, or timing details.',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
    icon: 'ClipboardList',
  },
  {
    id: 'command-center-tabs',
    title: 'Command Center — Workflow, Training & Ranking',
    description: 'The Workflow tab tracks ticket events and notes. The Training tab logs technician learning moments. The Ranking tab (manager-only) shows how OptiPlace scored and ranked technicians for this ticket assignment.',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
    icon: 'BarChart3',
  },
  {
    id: 'ticket-information',
    title: 'Ticket Information',
    description: 'Each ticket displays customer information, vehicle information, and jobs information. You can edit the time of a job directly from the ticket card by clicking on the job hours.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-customer-info', 'ticket-vehicle-info', 'ticket-jobs-list'],
  },
  {
    id: 'ticket-timing-explained',
    title: 'Understanding Ticket Times',
    description: 'Start shows when work begins. Tech OptiTime is the technician\'s estimated bay/work time, and OptiFinish is the final end time including buffer. Lifecycle Time (in the Ticket Lifecycle card) tracks the full ticket journey for visibility only — it does not change scheduling.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-time-section'],
  },
  {
    id: 'timer-controls',
    title: 'Timer Controls',
    description: 'The Play button starts tracking time, Pause temporarily stops the timer, and the Stop button completes the ticket and moves it to the Parking Lot.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-timer-controls'],
  },
  {
    id: 'drag-drop-demo',
    title: 'Assign with Drag & Drop',
    description: 'Drag tickets between technicians freely by clicking and holding the ⋮⋮ grip icon',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'ticket-kebab-menu',
    title: 'Ticket Options Menu',
    description: 'Click the three-dot menu (⋮) on any ticket to access additional actions',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-kebab-menu'],
  },
  {
    id: 'kebab-menu-options',
    title: 'Ticket Action Menu',
    description: 'The menu provides options to: Change Date (reschedule the ticket), Mark Vehicle Not Present (flag arrival status), Move to Parking Lot (unassign from technicians), Move Ticket (reassign to a different technician), Delete, and more. Select "Move to Parking Lot" to continue the tutorial.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['ticket-kebab-dropdown', 'move-job-submenu'],
  },
  {
    id: 'specialty-bays-intro',
    title: 'Specialty Bays Tab',
    description: 'The Specialty Bays tab shows work that requires specific bays like alignment racks, ADAS calibration bays, or detailing stations. Tickets needing these specialized resources are automatically organized here for easy tracking.',
    page: '/?tab=specialty-bays',
    gateType: 'soft',
    coachmarks: ['specialty-bays-tab'],
  },
  {
    id: 'specialty-bays-view',
    title: 'Specialty Bay Schedule',
    description: 'View all tickets requiring specialized bays. Each specialty bay shows its assigned work with timing and technician assignments.',
    page: '/?tab=specialty-bays',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'parking-lot-tab',
    title: 'Parking Lot Tab',
    description: 'The Parking Lot tab shows work that couldn\'t fit today or needs to be scheduled for future dates. Click the tab to view tickets waiting to be assigned.',
    page: '/?tab=specialty-bays',
    gateType: 'soft',
    coachmarks: ['parking-lot-tab'],
  },
  {
    id: 'parking-lot-view',
    title: 'Parking Lot',
    description: 'Vehicles that have been completed or need further repairs and have been physically moved to the parking lot are tracked here.',
    page: '/?tab=parking-lot',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'expand-parking-ticket',
    title: 'View Ticket Details',
    description: 'Click on a ticket in the Parking Lot to expand it and view full details including customer info, vehicle, and job list.',
    page: '/?tab=parking-lot',
    gateType: 'soft',
    coachmarks: ['parking-lot-ticket-card'],
  },
  {
    id: 'parking-ticket-kebab',
    title: 'Parking Lot Ticket Actions',
    description: 'Each ticket in the Parking Lot has a kebab menu ⋮ with options to reschedule, reassign, or manage the ticket. Click "Move Ticket" and select a technician to assign this ticket back to the board.',
    page: '/?tab=parking-lot',
    gateType: 'soft',
    coachmarks: ['ticket-kebab-menu', 'ticket-kebab-dropdown', 'move-job-submenu'],
  },
  {
    id: 'ticket-back-to-board',
    title: 'Ticket Restored to Schedule',
    description: 'The ticket has been moved from the Parking Lot back to the technician\'s column on the Dayboard. It\'s now scheduled and ready to be worked on.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'start-job-timer',
    title: 'Start Working on Jobs',
    description: 'Click the play button ▶ to start the timer for the first job on this ticket. This tracks actual time spent on each job.',
    page: '/?tab=dayboard',
    gateType: 'soft',
    coachmarks: ['timer-start'],
  },
  {
    id: 'optiboard-intro',
    title: 'OptiBoard - Ticket Lifecycle',
    description: 'OptiBoard gives you a Kanban-style view of all tickets across their lifecycle stages — from estimates to scheduled, work-in-progress, completed, picked up, and deleted. Track work from intake through completion at a glance.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['optiboard-link'],
    icon: 'LayoutDashboard',
  },
  {
    id: 'customers-intro',
    title: 'Search Tickets',
    description: 'Use the search bar at the top of the screen to quickly find tickets. Search by customer name, phone number, vehicle year/make/model, VIN, or license plate. Open it anytime with Ctrl+K (or Cmd+K on Mac). Selecting a result takes you straight to that ticket on the dayboard.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['search-bar'],
    icon: 'Search',
  },
  {
    id: 'birdseye-navigation',
    title: 'Visual Shop Layout',
    description: 'See your physical shop floor and bay assignments in Birdseye view',
    page: '/',
    gateType: 'soft',
    coachmarks: ['birdseye-link'],
  },
  {
    id: 'birdseye-view',
    title: 'Shop Floor Visualization',
    description: 'Visual representation of your shop\'s physical layout with real-time bay status',
    page: '/birdseye',
    gateType: 'soft',
    coachmarks: [],
  },
  {
    id: 'calendar-modal',
    title: 'Multi-Day Calendar',
    description: 'View appointments across multiple days and navigate your schedule. Click the inline date in the top bar to open the calendar, then select today\'s date to continue the tutorial.',
    page: '/',
    gateType: 'soft',
    // Standalone `calendar-button` was removed; clicking the inline
    // date in the top bar now opens the same CalendarModal.
    coachmarks: ['calendar-date-button'],
  },
  {
    id: 'analytics-intro',
    title: 'Analytics Dashboard',
    description: 'Access comprehensive performance tracking and insights. Click the Analytics link in the navigation to explore shop and team metrics.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['nav-analytics-button'],
    icon: 'BarChart3',
  },
  {
    id: 'analytics-technicians',
    title: 'Analytics Overview',
    description: 'Explore Technicians, Advisors, Jobs, Performance, Pause Analysis, Trends, Shop Health, and Alerts tabs for comprehensive insights. When finished, click your shop\'s name in the top left to navigate back to the dashboard.',
    page: '/analytics',
    gateType: 'soft',
    coachmarks: ['analytics-technicians-tab'],
    icon: 'Activity',
  },
  {
    id: 'technician-passport-intro',
    title: 'View Technician Passport',
    description: 'Click the technician\'s name on the dashboard to view the technician\'s passport.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['technician-passport-link'],
    icon: 'Award',
  },
  {
    id: 'technician-passport-details',
    title: 'Explore the Technician Passport',
    description: 'The passport includes: Snapshot Card (grade, strengths), Proficiency Radar (skill levels by category), Performance DNA (efficiency trends, diagnostic accuracy), Ticket Ledger (job history), and Coaching Notes (manager feedback).',
    page: '/technicians',
    gateType: 'soft',
    coachmarks: [],
    icon: 'BookOpen',
  },
  {
    id: 'account-settings-intro',
    title: 'Account Settings',
    description: 'Manage your personal preferences, notification settings, and account details. Access Account Settings from the user menu in the top right corner.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['account-settings-link'],
    icon: 'Settings',
  },
  {
    id: 'account-settings-review',
    title: 'Review Account Settings',
    description: 'Take a moment to explore the Account Settings page. Here you can update your display name, change your password, configure notification preferences, and manage your profile. When you\'re done, click Next to continue.',
    page: '/account-settings',
    gateType: 'soft',
    coachmarks: [],
    icon: 'Settings',
  },
  {
    id: 'user-guide-intro-user',
    title: 'Access the User Guide',
    description: 'Need help? The User Guide contains detailed documentation for every feature. Access it anytime from the account menu in the top right corner.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['user-guide-link'],
    icon: 'BookOpen',
  },
  {
    id: 'user-guide-review',
    title: 'Review User Guide',
    description: 'Take a moment to explore the User Guide. It contains detailed documentation for every feature in the app. Browse sections at your own pace. When you\'re done, click Next to continue.',
    page: '/user-guide',
    gateType: 'soft',
    coachmarks: [],
    icon: 'BookOpen',
  },
  {
    id: 'report-issue-intro',
    title: 'Report Issues',
    description: 'Found a bug? Use the draggable "Report an Issue" button (usually bottom-left or bottom-right) to submit feedback. Your position preference is saved.',
    page: '/',
    gateType: 'soft',
    coachmarks: ['report-issue-button'],
    icon: 'Bug',
  },
  {
    id: 'user-tutorial-complete',
    title: 'You\'re All Set!',
    description: 'You\'ve completed the User Tutorial! You now know how to: create tickets, manage schedules, use OptiAssign, track tickets in OptiBoard, view customer profiles, monitor performance in Analytics, explore the User Guide, and report issues. Start managing your shop efficiently!',
    page: '/',
    gateType: 'soft',
    coachmarks: [],
  },
];

/**
 * Legacy lookup, widened to the canonical experience vocabulary.
 *
 * Prefer `resolveExperienceSteps`, which applies role applicability and
 * cannot return an empty list. This remains for the surfaces that only need
 * a step count or a title and already hold a resolved flow.
 */
export const getStepsForFlow = (flow: OnboardingExperience): OnboardingStep[] => {
  if (flow === 'manager') return MANAGER_STEPS;
  if (flow === 'user') return USER_STEPS;
  return getRoleExperienceSteps(flow);
};

export const getStepById = (flow: OnboardingExperience, stepId: string): OnboardingStep | undefined => {
  const steps = getStepsForFlow(flow);
  return steps.find(step => step.id === stepId);
};

export const getStepByIndex = (flow: OnboardingExperience, index: number): OnboardingStep | undefined => {
  const steps = getStepsForFlow(flow);
  return steps[index];
};
